import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Project {
    name: string;
    description: string;
    logo: string;
    image: string;
    url: string;
}

interface CarouselProps {
    selectedProject: Project;
    isLoading: boolean;
    transitionSpeed: number;
    onImageLoad: () => void;
    onPause: () => void;
    onResume: () => void;
}

export const Carousel = ({ 
    selectedProject, 
    isLoading, 
    transitionSpeed, 
    onImageLoad,
    onPause,
    onResume
}: CarouselProps) => {
    return (
        <div 
            className="w-full relative max-lg:px-3"
            onMouseEnter={onPause}
            onMouseLeave={onResume}
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={selectedProject?.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isLoading ? 0 : 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: transitionSpeed / 1000 }}
                    className="h-full"
                >
                    <div className="relative h-full">
                        {/* Logo positioned at top left */}
                        {selectedProject?.logo && (
                            <div className="absolute top-4 left-4 z-10 bg-white p-2 rounded-md shadow-md">
                                <img
                                    src={selectedProject.logo}
                                    alt={`${selectedProject.name} logo`}
                                    className="w-12 h-12 object-contain"
                                />
                            </div>
                        )}
                        <img
                            src={selectedProject?.image}
                            alt={selectedProject?.name}
                            className="w-full h-full object-cover rounded-[4px]"
                            style={{ minHeight: "400px" }}
                            onLoad={onImageLoad}
                        />
                        <div 
                            className="absolute bottom-0 left-0 p-6 w-full h-[258px] flex flex-col justify-end"
                            style={{
                                background: "linear-gradient(180deg, rgba(251, 247, 244, 0.00) 0%, #FAF8F4 100%)"
                            }}
                        >
                            <motion.h2 
                                className="b_large_normal mb-[10.68px]"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.1, duration: 0.3 }}
                            >
                                {selectedProject?.name}
                            </motion.h2>
                            <motion.p 
                                className="b_regular_light mb-[21.35px]"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.2, duration: 0.3 }}
                            >
                                {selectedProject?.description}
                            </motion.p>
                            <motion.a
                                href={selectedProject?.url}
                                className="inline-flex items-center b_a1 w-full justify-between"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.3, duration: 0.3 }}
                            >
                                View Project <span className="ml-2 icon-right-arrow-alt text-[30px]"></span>
                            </motion.a>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-30 z-10">
                    <div className="spinner w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
        </div>
    );
};

export default Carousel;